'use client';
import React, { FC, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import type ReactPlayerType from 'react-player';
import { OnProgressProps } from 'react-player/base';
import { LoadingSpinner } from '@osmosis/tailwind-ui/components/LoadingSpinner';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { ZIndex } from '@osmosis/tailwind-ui/helpers/zIndex';
import { PROD_MEDIA_SERVER } from '@osmosis/constants';

import { TransformedAchievement } from '@osmosis/shared/types/achievement';
import { VideoComponentProps } from '@osmosis/shared/types/learn';

import { ReactPlayer } from './VideoPlayer';
import VideoControlBar from './VideoControlBar';
import VideoOverlay from './VideoOverlay';
import VideoPlaceholder from './VideoPlaceholder';
import VideoPoster from './VideoPoster';
import Subtitles from './Subtitles';

import useClientSide from '../../hooks/useClientSide';
import useDevice from '../../hooks/useDevice';
import useModal from '../../hooks/useModal';
import useVideoTracking from '../../hooks/useVideoTracking';
import { useOnboardingChecklist } from '../../context/OnboardingChecklistProvider';
import { usePagesContext } from '../../context/PagesProvider';
import { useLearnData } from '../../context/LearnDataProvider';
import { useVideoPlayer } from '../../context/VideoPlayerProvider';

interface HotKeysProps extends React.HTMLAttributes<HTMLDivElement> {
  keyMap: Record<string, string>;
  handlers: Record<string, (e: KeyboardEvent) => void>;
  allowChanges?: boolean;
}

type HotKeysType = FC<HotKeysProps>;

const AchievementInfoModal = dynamic(
  () => import('../achievements/AchievementInfoModal'),
  { ssr: false },
);

export const TRACKING_INTERVAL = 5; // 5 seconds

const videoStartedStyles: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
};

/**
 * Video element.
 */
export const Video = (
  props: VideoComponentProps & { pauseOverlay?: React.ReactNode },
): JSX.Element => {
  const {
    pauseOverlay,
    srcData,
    poster,
    captions,
    chapters,
    onProgress,
    onEnded,
    overlayComponents = [],
    learnNodeID,
    mediaID,
    isPrimeOnly,
    name,
    breadcrumbs,
    autoplay,
    isEmbed,
    showOAIButton,
  } = props;

  const {
    playing,
    playbackRate,
    volume,
    activeCaptions,
    startedState,
    isPictureInPicture,
    isFullscreen,
    hotkeyMap,
    hotkeyHandlers,
    progressState,
    getPlayer,
    setPlayer,
    setCaptions,
    setProgressState,
    buffering,
    setBuffering,
    handlePlay,
    setPictureInPicture,
  } = useVideoPlayer();

  const { isIOS } = useDevice();

  const { updateMediaStatus } = useVideoTracking();
  const { learnData: { watchStatus } = {} } = useLearnData();

  const [isStarted] = startedState;
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [hotKeysIsLoaded, setHotKeysIsLoaded] = useState(false);
  const [playerIsReady, setPlayerIsReady] = useState(false);
  const [autoplayed, setAutoplayed] = useState(false);

  const isClientSide = useClientSide();
  const { query } = usePagesContext();

  const hotkeyRef = useRef<HotKeysType | null>(null);
  const lastTrackedTime = useRef(0);

  const achievementModal = useModal<TransformedAchievement[]>();

  const { setVideoStepComplete } = useOnboardingChecklist();

  const forceHLS = !isIOS && srcData?.type === 'hls';

  const initHLS = async () => {
    if (!globalThis?.Hls) {
      const Hls = (await import('hls.js')).default;
      globalThis.Hls = Hls;
    }
    setPlayerIsReady(true);
  };

  useEffect(() => {
    if (!isClientSide || !srcData?.src) return;

    if (forceHLS) {
      initHLS();

      return;
    }
    setPlayerIsReady(true);
  }, [isClientSide, srcData, forceHLS]);

  const loadHotKeysLib = async () => {
    if (isClientSide) {
      const { HotKeys } = await import('react-hotkeys');
      hotkeyRef.current = HotKeys as unknown as HotKeysType;
      setHotKeysIsLoaded(true);
    }
  };

  useEffect(() => {
    if (getPlayer() && autoplay && playerIsReady && !autoplayed) {
      setAutoplayed(true);
      handlePlay();
    }
  }, [autoplay, handlePlay, playerIsReady, autoplayed]);

  useEffect(() => {
    loadHotKeysLib();
  }, [isClientSide]);

  useEffect(() => {
    // Handle initial state of video once it's loaded
    // watchStatus is only returned for logged-in users, so `t` must not depend on it
    if (isFirstLoading && getPlayer() && (query?.t || watchStatus)) {
      // Handle timestamp query parameter
      if (query?.t) {
        const timestamp = parseInt(query.t as string, 10);
        if (timestamp) {
          getPlayer()?.seekTo(timestamp, 'seconds');
        }
      } else if (watchStatus?.lastTime) {
        // Handle last time watched
        getPlayer()?.seekTo(watchStatus.lastTime, 'seconds');
        lastTrackedTime.current = watchStatus.lastTime;
      }

      setIsFirstLoading(false);
    }
    setCaptions(captions);
  }, [setPlayer, watchStatus]);

  const handleOnReady = (currentPlayer: ReactPlayerType): void => {
    setPlayer(currentPlayer);
  };

  /**
   * Track the user's status every 5 seconds.
   */
  const track = async (position: number) => {
    const roundedPosition = Math.floor(position);
    // Only track every 5 seconds
    if (
      position >= TRACKING_INTERVAL &&
      Math.abs(roundedPosition - lastTrackedTime.current) >= TRACKING_INTERVAL
    ) {
      if (
        await updateMediaStatus(
          { mediaID, isPrimeOnly, name, breadcrumbsv2: breadcrumbs },
          lastTrackedTime.current,
          query?.from as string,
          !!isEmbed,
          achievementModal.show,
        )
      ) {
        lastTrackedTime.current = roundedPosition;
      }
    }
  };

  const handleProgress = (state: OnProgressProps) => {
    const duration = getPlayer()?.getDuration();
    setProgressState(state);

    track(state.playedSeconds);

    setVideoStepComplete(
      learnNodeID,
      duration ? (state.playedSeconds / duration) * 100 : 0,
    );

    onProgress?.(state);
  };

  const handleBuffer = () => {
    if (isStarted && playing) {
      setBuffering(true);
    }
  };

  const handleBufferEnd = () => {
    if (buffering) {
      setBuffering(false);
    }
  };

  const videoHeight = isFullscreen ? 'calc(100vh - 70px)' : 'calc(100% - 68px)';

  const content = (
    <Box
      style={{
        position: 'relative',
        height: '100%',
        zIndex: ZIndex.BASE,
      }}
    >
      {srcData ? (
        <>
          <Box
            style={{
              position: 'relative',
              height: '100%',
              width: '100%',
            }}
          >
            {isClientSide && playerIsReady && (
              <ReactPlayer
                config={{
                  forceHLS,
                }}
                onReady={(currentPlayer: ReactPlayerType): void => {
                  handleOnReady(currentPlayer);
                }}
                onEnded={onEnded}
                onProgress={handleProgress}
                onBuffer={handleBuffer}
                onBufferEnd={handleBufferEnd}
                onEnablePIP={() => setPictureInPicture(true)}
                onDisablePIP={() => setPictureInPicture(false)}
                playing={playing}
                crossOrigin="true"
                volume={volume}
                playbackRate={playbackRate}
                url={srcData.src}
                width="100%"
                height={videoHeight}
                pip={isPictureInPicture}
                style={videoStartedStyles}
              />
            )}
            {!autoplay && !isStarted && <VideoPoster poster={poster} />}
          </Box>

          {buffering && (
            <Box className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
              <LoadingSpinner cn="text-6xl text-slate-600" />
            </Box>
          )}

          <Subtitles
            currentTime={progressState.playedSeconds * 1000}
            containerStyle="z-1 w-full"
            selectedSubtitle={{
              file: activeCaptions?.src
                ? `${PROD_MEDIA_SERVER}${activeCaptions?.src}`
                : '',
            }}
          />

          {pauseOverlay}

          <Box className="absolute bottom-0 z-20 w-full">
            <VideoControlBar
              captions={captions}
              chapters={chapters}
              showOAIButton={showOAIButton}
              className={props.classNames?.controlBar}
            />
          </Box>
          {(!autoplay || (autoplay && autoplayed)) && (
            <VideoOverlay components={[...overlayComponents]} />
          )}
        </>
      ) : (
        <>
          <VideoPoster poster={poster} />
          <VideoPlaceholder />
        </>
      )}
    </Box>
  );

  return (
    <>
      {!!achievementModal.data && achievementModal.isVisible && (
        <AchievementInfoModal
          achievements={achievementModal.data}
          {...achievementModal}
        />
      )}
      {hotKeysIsLoaded && hotkeyRef.current ? (
        <hotkeyRef.current
          keyMap={hotkeyMap}
          handlers={hotkeyHandlers}
          allowChanges={true}
          tabIndex={0}
          style={{ height: '100%' }}
          id="video-player-container"
        >
          {content}
        </hotkeyRef.current>
      ) : (
        <Box
          tabIndex={0}
          style={{ height: '100%' }}
          id="video-player-container"
        >
          {content}
        </Box>
      )}
    </>
  );
};
